import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  HiDocumentText, HiFolder, HiClipboardList,
  HiDownload, HiCheckCircle, HiBan,
} from 'react-icons/hi';
import { supabase } from '../supabase/client';
import { formatAmount, formatDate, formatDateLong } from '../utils/format';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const INVOICE_STATUS_STYLE = {
  Paid:    'bg-green-100  text-green-700',
  Pending: 'bg-yellow-100 text-yellow-700',
  Overdue: 'bg-red-100    text-red-600',
  Draft:   'bg-gray-100   text-gray-500',
};

const CONTRACT_STATUS_STYLE = {
  Draft:  'bg-gray-100  text-gray-600',
  Sent:   'bg-blue-100  text-blue-700',
  Signed: 'bg-green-100 text-green-700',
};

const FILE_ICON_COLOR = {
  'application/pdf':  'text-red-500 bg-red-50',
  'image/png':        'text-purple-500 bg-purple-50',
  'image/jpeg':       'text-purple-500 bg-purple-50',
  'application/zip':  'text-yellow-600 bg-yellow-50',
  'application/msword': 'text-blue-500 bg-blue-50',
};

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({ icon: Icon, title, count, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-50">
        <div className="w-9 h-9 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-indigo-500" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
          <p className="text-xs text-gray-400">{count} item{count !== 1 ? 's' : ''}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function EmptyRow({ text }) {
  return (
    <div className="px-6 py-10 text-center text-sm text-gray-400">{text}</div>
  );
}

// ─── Error / Inactive states ──────────────────────────────────────────────────

function PortalError({ icon: Icon, title, sub, iconBg, iconColor }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="text-center max-w-sm">
        <div className={`w-20 h-20 ${iconBg} rounded-3xl flex items-center justify-center mx-auto mb-5`}>
          <Icon className={`w-10 h-10 ${iconColor}`} />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">{title}</h2>
        <p className="text-sm text-gray-500 leading-relaxed">{sub}</p>
        <p className="text-xs text-gray-400 mt-6">Powered by <span className="font-semibold text-indigo-600">PortalFlow</span></p>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ClientPortal() {
  const { token } = useParams();

  const [client,          setClient]          = useState(null);
  const [invoices,        setInvoices]        = useState([]);
  const [files,           setFiles]           = useState([]);
  const [contracts,       setContracts]       = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [status,          setStatus]          = useState('loading'); // loading | ok | notfound | inactive
  const [payingId,        setPayingId]        = useState(null);
  const [freelancerUserId, setFreelancerUserId] = useState(null);

  useEffect(() => {
    const load = async () => {
      // 1. Look up portal by token
      const { data: portal, error } = await supabase
        .from('client_portals')
        .select('*, clients(*)')
        .eq('portal_token', token)
        .single();

      if (error || !portal) { setStatus('notfound'); setLoading(false); return; }
      if (!portal.is_active) { setStatus('inactive'); setLoading(false); return; }

      setClient(portal.clients);
      setFreelancerUserId(portal.user_id);
      const clientId = portal.client_id;

      // 2. Fetch invoices, shared files, contracts in parallel
      const [invRes, filesRes, contractsRes] = await Promise.all([
        supabase
          .from('invoices')
          .select('id, invoice_number, amount, status, due_date, created_at')
          .eq('client_id', clientId)
          .neq('status', 'Draft')
          .order('created_at', { ascending: false }),

        supabase
          .from('files')
          .select('id, name, size, type, path, created_at')
          .eq('client_id', clientId)
          .eq('shared_with_client', true)
          .order('created_at', { ascending: false }),

        supabase
          .from('contracts')
          .select('id, title, status, created_at, signed_at, content')
          .eq('client_id', clientId)
          .neq('status', 'Draft')
          .order('created_at', { ascending: false }),
      ]);

      setInvoices(invRes.data ?? []);
      setFiles(filesRes.data ?? []);
      setContracts(contractsRes.data ?? []);
      setStatus('ok');
      setLoading(false);
    };

    load();
  }, [token]);

  // ── Pay invoice ────────────────────────────────────────────────────────────

  const handlePayInvoice = async (inv) => {
    setPayingId(inv.id);
    try {
      const res = await fetch('/api/payments/create-checkout-session', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoiceId:        inv.id,
          amount:           inv.amount,
          clientEmail:      client?.email,
          invoiceNumber:    inv.invoice_number,
          freelancerUserId: freelancerUserId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      window.location.href = data.sessionUrl;
    } catch (err) {
      alert('Payment error: ' + err.message);
      setPayingId(null);
    }
  };

  // ── File download via backend ───────────────────────────────────────────────

  const downloadFile = async (file) => {
    try {
      const res = await fetch(`/api/portal/${token}/file-url`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ filePath: file.path }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      const a = document.createElement('a');
      a.href = json.url;
      a.download = file.name;
      a.click();
    } catch (err) {
      alert('Download failed: ' + err.message);
    }
  };

  // ── States ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <svg className="animate-spin h-10 w-10 text-indigo-500" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
      </div>
    );
  }

  if (status === 'notfound') return (
    <PortalError
      icon={HiBan}
      iconBg="bg-red-50" iconColor="text-red-400"
      title="Portal Not Found"
      sub="This portal link is invalid or has been removed. Please contact your service provider for a new link."
    />
  );

  if (status === 'inactive') return (
    <PortalError
      icon={HiBan}
      iconBg="bg-yellow-50" iconColor="text-yellow-500"
      title="Portal Deactivated"
      sub="This client portal has been temporarily deactivated. Please contact your service provider to regain access."
    />
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Top nav */}
      <header className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">P</span>
            </div>
            <span className="font-bold text-gray-900 text-lg tracking-tight">PortalFlow</span>
          </div>
          <span className="text-xs font-medium text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
            Client Portal
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">

        {/* Welcome banner */}
        <div className="bg-indigo-600 rounded-2xl px-8 py-7 text-white">
          <p className="text-indigo-300 text-xs font-semibold uppercase tracking-wider mb-1">
            Welcome back
          </p>
          <h1 className="text-2xl font-bold mb-1">{client?.name}</h1>
          <p className="text-indigo-200 text-sm">
            Your invoices, files, and contracts are all in one place below.
          </p>
          {client?.company && (
            <p className="text-indigo-300 text-xs mt-2">{client.company}</p>
          )}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Invoices',  count: invoices.length,  icon: HiDocumentText, color: 'bg-blue-50   text-blue-500' },
            { label: 'Files',     count: files.length,     icon: HiFolder,       color: 'bg-purple-50 text-purple-500' },
            { label: 'Contracts', count: contracts.length, icon: HiClipboardList, color: 'bg-green-50  text-green-500' },
          ].map(({ label, count, icon: Icon, color }) => (
            <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-center">
              <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center mx-auto mb-2`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{count}</p>
              <p className="text-xs text-gray-500 mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        {/* ── Invoices ── */}
        <Section icon={HiDocumentText} title="Your Invoices" count={invoices.length}>
          {invoices.length === 0 ? (
            <EmptyRow text="No invoices yet" />
          ) : (
            <div className="divide-y divide-gray-50">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex items-center gap-4 px-6 py-4">
                  <div className="w-9 h-9 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <HiDocumentText className="w-4 h-4 text-indigo-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-indigo-600">{inv.invoice_number}</p>
                    <p className="text-xs text-gray-400">Due {formatDate(inv.due_date)}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <p className="text-sm font-bold text-gray-900">{formatAmount(inv.amount)}</p>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${INVOICE_STATUS_STYLE[inv.status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {inv.status}
                    </span>
                    {(inv.status === 'Pending' || inv.status === 'Overdue') && (
                      <button
                        onClick={() => handlePayInvoice(inv)}
                        disabled={payingId === inv.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold
                          text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition disabled:opacity-60"
                      >
                        {payingId === inv.id ? (
                          <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                          </svg>
                        ) : '💳'} Pay
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ── Files ── */}
        <Section icon={HiFolder} title="Shared Files" count={files.length}>
          {files.length === 0 ? (
            <EmptyRow text="No shared files yet" />
          ) : (
            <div className="divide-y divide-gray-50">
              {files.map((file) => {
                const iconStyle = FILE_ICON_COLOR[file.type] ?? 'text-gray-400 bg-gray-100';
                return (
                  <div key={file.id} className="flex items-center gap-4 px-6 py-4">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconStyle}`}>
                      <HiFolder className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">{file.name}</p>
                      <p className="text-xs text-gray-400">{formatSize(file.size)} · {formatDate(file.created_at)}</p>
                    </div>
                    <button
                      onClick={() => downloadFile(file)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold
                        text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition flex-shrink-0"
                    >
                      <HiDownload className="w-3.5 h-3.5" /> Download
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* ── Contracts ── */}
        <Section icon={HiClipboardList} title="Your Contracts" count={contracts.length}>
          {contracts.length === 0 ? (
            <EmptyRow text="No contracts yet" />
          ) : (
            <div className="divide-y divide-gray-50">
              {contracts.map((contract) => (
                <div key={contract.id} className="flex items-center gap-4 px-6 py-4">
                  <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <HiClipboardList className="w-4 h-4 text-green-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">{contract.title}</p>
                    <p className="text-xs text-gray-400">
                      {contract.signed_at
                        ? `Signed ${formatDateLong(contract.signed_at)}`
                        : `Created ${formatDate(contract.created_at)}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CONTRACT_STATUS_STYLE[contract.status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {contract.status === 'Signed' ? (
                        <span className="flex items-center gap-1">
                          <HiCheckCircle className="w-3 h-3" /> Signed
                        </span>
                      ) : contract.status}
                    </span>
                    <button
                      onClick={() => window.print()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold
                        text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Footer */}
        <div className="text-center pt-4 pb-8">
          <p className="text-xs text-gray-400">
            Powered by{' '}
            <span className="font-semibold text-indigo-600">PortalFlow</span>
            {' '}· Secure client portal
          </p>
        </div>

      </main>
    </div>
  );
}
